import { c as _c } from "react/compiler-runtime";
import { Children, MouseEvent, MouseEventHandler, ReactElement, ReactNode, isValidElement, memo } from 'react';
import trackPromotionActions, { PromotionEvent, trackingParamKeys } from '@spa/lib/tracking/trackPromotionActions';
import useLinkAttributes, { LinkParam, Link as LinkProp, LinkTarget } from '@spa/lib/react/hooks/useLinkAttributes';
import Link from 'next/link';
import { PageTemplate } from '@spa/lib/cms/const';
import getAuthSessionUserId from '@spa/lib/auth/getAuthSessionUserId';
import selectPageTemplate from '@spa/lib/store/router/selectors/selectPageTemplate';
import { useIntersectionObserver } from 'usehooks-ts';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
import { usePathname } from 'next/navigation';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
import { useSelector } from 'react-redux';
export type LinkComponentLink = LinkProp & {
  noTrackIntersection?: boolean;
};
export type LinkComponentLinkTarget = LinkTarget;
export const getNoTrackIntersectionLink = (link: LinkComponentLink | null): LinkComponentLink | null => {
  return link ? {
    ...link,
    noTrackIntersection: true
  } : null;
};
export interface Props {
  readonly link: LinkComponentLink;
  readonly fallbackTitle?: string;
  readonly ariaLabelledBy?: string;
  readonly visibleText?: string;
  readonly children?: ReactNode;
  readonly className?: string;
  readonly preventForwarding?: boolean;
  readonly onClick?: MouseEventHandler<HTMLAnchorElement>;
  readonly dataTestId?: string;
}
function getTextFromChildren(children: ReactNode): string {
  return Children.toArray(children).map((child): string => {
    if (typeof child === 'string' || typeof child === 'number') {
      return String(child);
    }
    if (isValidElement<{
      children?: ReactNode;
    }>(child)) {
      return getTextFromChildren(child.props.children);
    }
    return '';
  }).join('');
}
function LinkComponent(t0) {
  const $ = _c(44);
  const {
    children,
    fallbackTitle,
    ariaLabelledBy,
    visibleText,
    link,
    className,
    preventForwarding,
    onClick,
    dataTestId
  } = t0;
  const pathname = usePathname();
  const searchParams = useRuntimeSearchParams();
  const isRedirected = searchParams?.get("redirected") === "true";
  const pageTemplate = useSelector(selectPageTemplate);
  const {
    linkParams,
    noTrackIntersection
  } = link;
  let nonTrackingParams;
  let trackingParams;
  if ($[0] !== linkParams) {
    trackingParams = [];
    nonTrackingParams = [];
    linkParams.forEach(linkParam => {
      if (trackingParamKeys.includes(linkParam.key)) {
        trackingParams.push(linkParam);
      } else {
        nonTrackingParams.push(linkParam);
      }
    });
    $[0] = linkParams;
    $[1] = nonTrackingParams;
    $[2] = trackingParams;
  } else {
    nonTrackingParams = $[1];
    trackingParams = $[2];
  }
  const hasTrackingParams = trackingParams.length !== 0;
  let t1;
  if ($[3] !== link || $[4] !== nonTrackingParams) {
    t1 = {
      ...link,
      linkParams: nonTrackingParams
    };
    $[3] = link;
    $[4] = nonTrackingParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const linkAttributes = useLinkAttributes(t1, fallbackTitle);
  let t2;
  if ($[6] !== linkAttributes) {
    t2 = linkAttributes ?? {
      href: "#"
    };
    $[6] = linkAttributes;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let linkAttributesWithoutTitle;
  let title;
  if ($[8] !== t2) {
    ({
      title,
      ...linkAttributesWithoutTitle
    } = t2);
    $[8] = t2;
    $[9] = linkAttributesWithoutTitle;
    $[10] = title;
  } else {
    linkAttributesWithoutTitle = $[9];
    title = $[10];
  }
  const linkText = getTextFromChildren(children).trim();
  const accessibleText = visibleText?.trim() || linkText;
  let t3;
  if ($[11] !== title) {
    t3 = typeof title === "string" && title.trim() !== "" ? title.trim() : undefined;
    $[11] = title;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const normalizedTitle = t3;
  const shouldRenderTitle = normalizedTitle !== undefined && normalizedTitle !== accessibleText;
  const shouldRenderAriaLabel = ariaLabelledBy === undefined && accessibleText === "" && normalizedTitle !== undefined;
  const {
    authSession,
    session
  } = useNextAuthOrSession();
  const anonymousUserId = session.guid;
  let t4;
  if ($[13] !== authSession) {
    t4 = getAuthSessionUserId(authSession);
    $[13] = authSession;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const userId = t4;
  let reference = undefined;
  let t5;
  if ($[15] !== anonymousUserId || $[16] !== hasTrackingParams || $[17] !== noTrackIntersection || $[18] !== pageTemplate || $[19] !== pathname || $[20] !== trackingParams || $[21] !== userId) {
    t5 = {
      threshold: 0.5,
      freezeOnceVisible: true,
      onChange: isIntersecting => !noTrackIntersection && hasTrackingParams && isIntersecting && trackPromotionActions(PromotionEvent.VIEW, anonymousUserId, userId, trackingParams, {
        pathname,
        pageTemplate
      })
    };
    $[15] = anonymousUserId;
    $[16] = hasTrackingParams;
    $[17] = noTrackIntersection;
    $[18] = pageTemplate;
    $[19] = pathname;
    $[20] = trackingParams;
    $[21] = userId;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const {
    ref
  } = useIntersectionObserver(t5);
  if (hasTrackingParams && !noTrackIntersection) {
    reference = ref;
  }
  let t6;
  if ($[23] !== anonymousUserId || $[24] !== hasTrackingParams || $[25] !== onClick || $[26] !== pageTemplate || $[27] !== pathname || $[28] !== trackingParams || $[29] !== userId) {
    t6 = function onLinkClicked(event) {
      onClick?.(event);
      if (hasTrackingParams) {
        trackPromotionActions(PromotionEvent.SELECT, anonymousUserId, userId, trackingParams, {
          pathname,
          pageTemplate
        });
      }
    };
    $[23] = anonymousUserId;
    $[24] = hasTrackingParams;
    $[25] = onClick;
    $[26] = pageTemplate;
    $[27] = pathname;
    $[28] = trackingParams;
    $[29] = userId;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const onLinkClicked = t6;
  const t7 = shouldRenderAriaLabel ? normalizedTitle : undefined;
  const t8 = shouldRenderTitle ? normalizedTitle : undefined;
  const t9 = preventForwarding ? -1 : 0;
  const t10 = isRedirected ? "redirectSubSelector" : undefined;
  let t11;
  if ($[31] !== ariaLabelledBy || $[32] !== children || $[33] !== className || $[34] !== dataTestId || $[35] !== linkAttributesWithoutTitle || $[36] !== onLinkClicked || $[37] !== preventForwarding || $[38] !== reference || $[39] !== t10 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t11 = <Link aria-label={t7} aria-labelledby={ariaLabelledBy} {...linkAttributesWithoutTitle} title={t8} prefetch={false} ref={reference} aria-disabled={preventForwarding} className={className} tabIndex={t9} onClick={onLinkClicked} data-track-id={t10} data-testid={dataTestId}>{children}</Link>;
    $[31] = ariaLabelledBy;
    $[32] = children;
    $[33] = className;
    $[34] = dataTestId;
    $[35] = linkAttributesWithoutTitle;
    $[36] = onLinkClicked;
    $[37] = preventForwarding;
    $[38] = reference;
    $[39] = t10;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
}
export default memo(LinkComponent);